import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  updateProfile
} from 'firebase/auth';
import { auth } from '../firebase';
import api from '../api/axios';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCompleteProfile, setShowCompleteProfile] = useState(false);
  const [profileIncomplete, setProfileIncomplete] = useState(false);

  const signup = async (email, password, profileData) => {
    // Create Firebase Auth user
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Update display name in Firebase Auth
    if (profileData.name) {
      await updateProfile(user, {
        displayName: profileData.name
      });
    }

    // Register user in backend with profile data
    // Backend will create the Firestore document
    try {
      await api.post('/auth/register', {
        display_name: profileData.name || null,
        phone: profileData.phone || null,
        country: profileData.country || null,
        state: profileData.state || null
      });
      console.log('User registered in backend successfully');
    } catch (error) {
      console.error('Backend registration error:', error);
      // If backend registration fails, the user is still created in Auth
      // They can complete their profile later
    }

    return userCredential;
  };

  const login = (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  const loginWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    const userCredential = await signInWithPopup(auth, provider);
    const user = userCredential.user;

    let needsProfileCompletion = false;

    try {
      const response = await api.post('/auth/register', {
        display_name: user.displayName || null,
        phone: user.phoneNumber || null,
        country: null,
        state: null
      });
      
      console.log('Google user registered in backend successfully');
      
      if (response.data.data.profile_incomplete) {
        needsProfileCompletion = true;
        setProfileIncomplete(true);
        setShowCompleteProfile(true);
      }
    } catch (error) {
      console.error('Backend registration error:', error);
    }

    return { userCredential, needsProfileCompletion };
  };

  const completeProfile = async (profileData) => {
    try {
      await api.put('/auth/profile', profileData);
      setShowCompleteProfile(false);
      setProfileIncomplete(false);
      console.log('Profile completed successfully');
    } catch (error) {
      console.error('Profile completion error:', error);
      throw error;
    }
  };

  const logout = () => {
    return signOut(auth);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    signup,
    login,
    loginWithGoogle,
    logout,
    loading,
    showCompleteProfile,
    setShowCompleteProfile,
    completeProfile,
    profileIncomplete
  };

  // Render right away rather than waiting on Firebase (which on mobile and
  // Safari first loads a Google auth iframe). Anything that needs the restored
  // user waits on `loading`: PrivateRoute, AdminRoute, AuthReady in App.js, and
  // the api client's request interceptor.
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
